// src/api/apps.ts

import api from '@/lib/axios';
import type {
  AppInstance,
  CreateAppRequest,
  CreateAppResponse,
  ResetKeyResponse,
  AddSHA256Request,
  AndroidSHA256Certificate,
  SHA256ListResponse,
} from '@/types/app';

/**
 * 创建 App（含双端密钥）
 * POST /v1/developer/apps
 */
export const createApp = (requestData: CreateAppRequest): Promise<CreateAppResponse> => {
  return api.post('/developer/apps', requestData);
};

/**
 * 获取 App 列表
 * GET /v1/developer/apps
 */
export const getApps = (): Promise<AppInstance[]> => {
  return api.get('/developer/apps');
};

/**
 * 获取 App 详情
 * GET /v1/developer/apps/{app_id}
 * @param appId - App ID
 */
export const getApp = (appId: string): Promise<AppInstance> => {
  return api.get(`/developer/apps/${appId}`);
};

/**
 * 重置 iOS 密钥
 * POST /v1/developer/apps/{app_id}/keys/ios/secret/reset
 * @param appId - App ID
 */
export const resetIOSKey = (appId: string): Promise<ResetKeyResponse> => {
  return api.post(`/developer/apps/${appId}/keys/ios/secret/reset`);
};

/**
 * 重置 Android 密钥
 * POST /v1/developer/apps/{app_id}/keys/android/secret/reset
 * @param appId - App ID
 */
export const resetAndroidKey = (appId: string): Promise<ResetKeyResponse> => {
  return api.post(`/developer/apps/${appId}/keys/android/secret/reset`);
};

/**
 * 删除 App
 * DELETE /v1/developer/apps/{app_id}
 * @param appId - App ID
 */
export const deleteApp = (appId: string): Promise<void> => {
  return api.delete(`/developer/apps/${appId}`);
};

/**
 * 添加 Android SHA256 证书
 * POST /v1/developer/apps/{app_id}/android/sha256
 * @param appId - App ID
 * @param certificateData - 证书数据
 */
export const addAndroidSHA256 = (
  appId: string,
  certificateData: AddSHA256Request
): Promise<AndroidSHA256Certificate> => {
  return api.post(`/developer/apps/${appId}/android/sha256`, certificateData);
};

/**
 * 获取 Android SHA256 证书列表
 * GET /v1/developer/apps/{app_id}/android/sha256
 * @param appId - App ID
 */
export const getAndroidSHA256 = (appId: string): Promise<SHA256ListResponse> => {
  return api.get(`/developer/apps/${appId}/android/sha256`);
};

/**
 * 删除 Android SHA256 证书
 * DELETE /v1/developer/apps/{app_id}/android/sha256/{certificate_id}
 * @param appId - App ID
 * @param certificateId - 证书 ID
 */
export const deleteAndroidSHA256 = (appId: string, certificateId: string): Promise<void> => {
  return api.delete(`/developer/apps/${appId}/android/sha256/${certificateId}`);
};

/**
 * 获取 App 关联的产品列表
 * GET /v1/developer/apps/{app_id}/products
 * @param appId - App ID
 */
export const getProductsByApp = (appId: string): Promise<Product[]> => {
  return api.get(`/developer/apps/${appId}/products`);
};

// 为关联产品接口引入 Product 类型
import type { Product } from '@/types/product';

/**
 * 绑定产品到 App
 * POST /v1/developer/apps/{app_id}/products/{product_id}/bind
 * @param appId - App ID
 * @param productId - 产品 ID
 */
export const bindProductToApp = (appId: string, productId: string): Promise<void> => {
  return api.post(`/developer/apps/${appId}/products/${productId}/bind`);
};

/**
 * 解绑产品从 App
 * DELETE /v1/developer/apps/{app_id}/products/{product_id}/unbind
 * @param appId - App ID
 * @param productId - 产品 ID
 */
export const unbindProductFromApp = (appId: string, productId: string): Promise<void> => {
  return api.delete(`/developer/apps/${appId}/products/${productId}/unbind`);
};

/**
 * 获取所有产品（用于绑定选择）
 * GET /v1/developer/products?page=1&size=1000
 */
export const getAllProductsForBinding = async (): Promise<Product[]> => {
  const response = await api.get('/developer/products?page=1&size=1000');
  return response.data.items;
};